import * as globals from '@wandb/weave/common/css/globals.styles';
import Color from 'color';
import type uPlot from 'uplot';

import {formatXAxisNonTime, formatYAxis} from '../../../util/plotHelpers/axis';
import {
  getAxisStyleForFontSize,
  type PlotFontSize,
} from '../../../util/plotHelpers/plotFontSize';
import {type Line, type Timestep} from '../../../util/plotHelpers/types';
import {hasMultipleSinglePointRuns} from '../../PanelRunsLinePlot/utils/checkTypes';
import {getStrokeDashArrayNonSvg, getStrokeDetail} from '../lineUtils';
import {type DomainMaybe} from '../types';
import {
  createDrawIssueLinesHook,
  type IssueLinesConfig,
} from './drawIssueLines';
import {
  createDrawNonFiniteMarkersHook,
  getNonFiniteMarkers,
  type NonFiniteMarker,
  type NonFiniteMarkerState,
} from './drawNonFiniteMarkers';
import {getBands} from './getBands';
import {getDomain} from './getDomain';
import {getIncrs} from './getIncrs';
import {type LineNameMap, type SeriesEntry} from './getSeriesEntries';
import {
  getuPlotLineGap,
  getuPlotTickFontSizePx,
  getuPlotTickGapPx,
  getuPlotTickSizePx,
  getuPlotXAxisTickValuesSizePx,
  getuPlotYAxisTickValuesSizePx,
} from './margins';
import {numAxisSplitsForUPlotAxis} from './splits';

const GRID_LINE_WIDTH_PX = 1;
const POINT_PX_SIZE = 4;
const SINGLE_POINT_PX_SIZE = 8;
export const getGridLineColor = (isDarkMode: boolean) => {
  return isDarkMode
    ? Color(globals.MOON_750).rgb().string()
    : Color(globals.MOON_250).rgb().string();
};

export type NonFiniteMarkerContext = {
  markers: NonFiniteMarker[];
  stateRef: React.RefObject<NonFiniteMarkerState>;
};

// uPlot does not render a visible mark for a line series unless points are enabled.
// This callback shows a marker only when the current visible range contains exactly
// one finite point, which preserves the normal line-only rendering for dense series.
export function shouldShowSingleVisiblePoint(
  u: uPlot,
  seriesIdx: number,
  idx0: number,
  idx1: number
): boolean {
  const yValues = u.data[seriesIdx] as ArrayLike<number | null | undefined>;
  let finitePointCount = 0;

  for (let i = idx0; i <= idx1; i++) {
    const y = yValues[i];

    if (y != null && Number.isFinite(y) && ++finitePointCount > 1) {
      return false;
    }
  }

  return finitePointCount === 1;
}

export function getUPlotOptions(
  seriesEntries: (SeriesEntry | null)[],
  lineNameMap: LineNameMap,
  lines: Line[],
  nonFiniteMarkerStateRef: React.RefObject<NonFiniteMarkerState>,
  issueLinesConfig: IssueLinesConfig,
  width: number,
  height: number,
  xDomain: DomainMaybe,
  yDomain: DomainMaybe,
  xAxisIsDatetime: boolean | undefined,
  xLogScale: boolean | undefined,
  yLogScale: boolean | undefined,
  fontSize: PlotFontSize,
  timestep: Timestep | undefined,
  isDarkMode: boolean,
  isOverlay: boolean = false,
  isBaseLayer: boolean = false
): uPlot.Options {
  const bands = getBands(seriesEntries, lineNameMap);
  const isSinglePointChart = lines.length === 1 && lines[0].data.length === 1;
  const showEnlargedSinglePoint =
    isSinglePointChart || hasMultipleSinglePointRuns(lines);
  // Handle infinite/NaN markers
  const nonFiniteMarkers = getNonFiniteMarkers(seriesEntries);
  const drawNonFiniteMarkers = createDrawNonFiniteMarkersHook(
    nonFiniteMarkers,
    nonFiniteMarkerStateRef
  );

  // Handle issue lines
  const drawIssueLines = createDrawIssueLinesHook(issueLinesConfig);

  // Keep the overlay's complete series structure stable. Hovering only toggles
  // per-series visibility in LinePlotuPlot, so it does not rebuild options.
  const initialSeriesShow = !isOverlay;

  // First series config is for x-axis (empty object)
  // Then one config per line
  const seriesConfigs: uPlot.Series[] = seriesEntries.map(entry => {
    if (entry == null) {
      // x-axis series
      return {};
    }
    const {type, line} = entry;
    const baseColor = line.color ?? 'red';
    const strokeDashArray = getStrokeDashArrayNonSvg(line.mark)?.map(
      num => num * window.devicePixelRatio
    );
    const {strokeWidth: lineStrokeWidth} = getStrokeDetail(
      line.color,
      isOverlay, // the axes are hidden in the overlay layer which is the highlighted layer
      line.lineWidth
    );

    if (type === 'band-min' || type === 'band-max') {
      // Band boundary series - needs visible stroke for uPlot bands to work
      return {
        show: initialSeriesShow,
        spanGaps: true,
        stroke: Color(baseColor).rgb().string(),
        width: lineStrokeWidth,
        label: line.name,
        points: {show: false},
        sorted: 1, // Series.Sorted.Ascending - we pre-sort the data in getAlignedData, which allows some optimizations for uplot
      };
    } else {
      // Primary line series - full opacity. Dimming is handled by the useEffect in LinePlotuPlot.
      const stroke = Color(baseColor).rgb().string();
      return {
        show: initialSeriesShow,
        spanGaps: true,
        stroke,
        width: lineStrokeWidth,
        label: line.name,
        sorted: 1, // Series.Sorted.Ascending - we pre-sort the data in getAlignedData, which allows some optimizations for uplot
        dash: strokeDashArray,
        points: {
          show: shouldShowSingleVisiblePoint,
          size: showEnlargedSinglePoint ? SINGLE_POINT_PX_SIZE : POINT_PX_SIZE,
          width: 1,
          stroke,
          fill: stroke,
        },
      };
    }
  });

  const tickLabelStyle = getAxisStyleForFontSize(fontSize);
  const fontString = `${getuPlotTickFontSizePx(fontSize)}px ${tickLabelStyle.fontFamily}`;

  const gridLineColor = getGridLineColor(isDarkMode);
  const renderAxisChrome = isBaseLayer;
  // Only the base layer paints visible axis values; other layers draw them
  // transparent (but still generate the values so axis sizing stays identical).
  const axisValueColor = renderAxisChrome
    ? (tickLabelStyle.fill as string)
    : 'transparent';
  const sharedAxisOptions = {
    gap: getuPlotTickGapPx(),
    lineGap: getuPlotLineGap(),
    font: fontString,
    border: {
      show: renderAxisChrome,
      stroke: gridLineColor,
      width: GRID_LINE_WIDTH_PX,
    },
  };

  const xAxisValues = (function computeXAxisValues() {
    if (xAxisIsDatetime) {
      return undefined;
    }
    return function formatXAxisValues(_u: uPlot, splits: number[]) {
      if (xDomain[0] == null || xDomain[1] == null) {
        return splits;
      }
      const formatter = formatXAxisNonTime(
        xLogScale ? 'log' : 'linear',
        xDomain[0]!,
        xDomain[1]!,
        timestep
      );
      if (formatter == null) {
        return splits;
      }
      // Format every split once, collecting both the aligned labels (nulls kept
      // so the array stays 1:1 with splits, for rendering) and the non-null
      // labels (for the duplicate check) in a single pass.
      const {formattedSplits, filteredFormattedSplits} = splits.reduce<{
        formattedSplits: (string | null)[];
        filteredFormattedSplits: string[];
      }>(
        (acc, split) => {
          if (split == null) {
            acc.formattedSplits.push(null);
            return acc;
          }
          const formatted = formatter(split);
          acc.formattedSplits.push(formatted);
          acc.filteredFormattedSplits.push(formatted);
          return acc;
        },
        {formattedSplits: [], filteredFormattedSplits: []}
      );
      // Just like in react-vis https://github.com/wandb/core/pull/22703
      if (
        new Set(filteredFormattedSplits).size !== filteredFormattedSplits.length
      ) {
        // duplicate x values, so actually just return the original splits
        return splits;
      }
      return formattedSplits;
    };
  })();
  const yAxisValues = (_u: uPlot, splits: number[]) => {
    return splits.map(split => (split == null ? null : formatYAxis(split)));
  };

  const result: uPlot.Options = {
    width: width ?? 400,
    height: height ?? 400,
    series: seriesConfigs,
    bands,
    padding: [8, 12, 0, 0], // uPlot defaults to extra padding we don't need
    axes: [
      // x-axis
      {
        stroke: axisValueColor, // this is a string, not just any CSSProperties
        values: xAxisValues,
        grid: {
          show: false,
        },
        size: getuPlotXAxisTickValuesSizePx(fontSize),
        incrs: getIncrs(xAxisIsDatetime ?? false),
        ticks: {
          show: renderAxisChrome,
          size: getuPlotTickSizePx(),
          stroke: gridLineColor,
          width: GRID_LINE_WIDTH_PX,
        },
        splits: xLogScale ? undefined : numAxisSplitsForUPlotAxis,
        ...sharedAxisOptions,
      },
      // y-axis
      {
        stroke: axisValueColor, // this is a string, not just any CSSProperties
        values: yAxisValues,
        size: getuPlotYAxisTickValuesSizePx(fontSize),
        grid: {
          show: renderAxisChrome,
          stroke: gridLineColor,
          width: GRID_LINE_WIDTH_PX,
        },
        splits: yLogScale ? undefined : numAxisSplitsForUPlotAxis,
        incrs: getIncrs(false),
        ticks: {
          show: renderAxisChrome,
          size: getuPlotTickSizePx(),
          stroke: gridLineColor,
          width: GRID_LINE_WIDTH_PX,
          filter: (u, splits, axisIdx, foundSpace, foundIncr) =>
            u.axes[axisIdx]?.filter?.(
              u,
              splits,
              axisIdx,
              foundSpace,
              foundIncr
            ) ?? splits,
        },
        ...sharedAxisOptions,
      },
    ],
    scales: {
      x: {
        time: xAxisIsDatetime ?? false,
        distr: xLogScale ? 3 : 1, // Scale.Distr.Logarithmic = 3, Scale.Distr.Linear = 1
        range: getDomain(xDomain, xLogScale ?? false),
      },
      y: {
        distr: yLogScale ? 3 : 1, // Scale.Distr.Logarithmic = 3, Scale.Distr.Linear = 1
        range: getDomain(yDomain, yLogScale ?? false),
      },
    },
    legend: {
      show: false,
    },
    // The React crosshair overlay sits on top of the plot and handles hover and
    // brush-zoom, so uPlot's own cursor never sees a mouse event. Turning it off
    // skips creating a hover point element for every series and skips the
    // transform writes uPlot makes on those elements whenever it updates the
    // cursor. Nothing reads uPlot cursor state (no setCursor hooks, no reads of
    // u.cursor), so this only removes work.
    cursor: {
      show: false,
    },
    hooks: {
      draw: [drawIssueLines, drawNonFiniteMarkers],
    },
    ms: 1, // uPlot uses seconds by default, but the Line data is in milliseconds
  };
  return result;
}
